/**
 * Privacy policy and terms of service.
 *
 * Written for the person who has to agree to them, which mostly means written
 * short and in sentences. A policy nobody finishes is not consent, and length
 * is the usual way of hiding what a service actually does.
 *
 * Two rules held throughout:
 *
 *  1. Every statement describes what the code does. Where the architecture
 *     makes a promise structural, it is said plainly; where it does not, the
 *     exception is named rather than buried. The unencrypted avatar, the
 *     unrecoverable password, the group recovery key and lawful access are all
 *     in here for that reason.
 *
 *  2. Nothing is disclaimed that cannot lawfully be disclaimed. A controller
 *     cannot contract out of Article 82 liability towards a data subject, and
 *     a consumer's statutory rights survive any clause to the contrary, so the
 *     limitation section says what it can actually do and says so openly.
 *     Writing a blanket "not responsible for anything" clause would be both
 *     unenforceable and, on a page about trust, self-defeating.
 *
 * Not legal advice and not reviewed by a lawyer. These should be, before the
 * service takes money.
 */

import { Link } from "@tanstack/react-router";
import { PRODUCT_NAME } from "../../components/ui/Logo.js";
import "./LegalPage.css";

/** Update when the substance changes, not when a typo is fixed. */
const UPDATED = "2 September 2026";

/**
 * The controller, named once and reused.
 *
 * GDPR Article 13(1)(a) wants the identity and contact details of the
 * controller, which means the registered company rather than the brand on the
 * front page. Both are given so nobody has to guess they are the same thing.
 */
const COMPANY = {
  legalName: "Source Byte Sp. z o.o.",
  street: "Warszawska 40/2A",
  postcode: "40-008",
  city: "Katowice",
  country: "Poland",
  email: "admin@source-byte.com",
} as const;

/* ═══════════════════════════════ Privacy ═════════════════════════════════ */

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy policy" updated={UPDATED}>
      <p className="fc-legal__lead">
        The short version: your photographs and videos are encrypted on your own
        devices before they reach us, the ordinary running of this service never
        opens them, and nothing here is sold, shared for advertising, or used to
        train anything. Two exceptions exist — a group’s recovery key, and a
        lawful order — and both are described below rather than left out.
      </p>

      <h2>Who is responsible for your data</h2>
      <p>
        {PRODUCT_NAME} is operated by <strong>{COMPANY.legalName}</strong>,{" "}
        {COMPANY.street}, {COMPANY.postcode} {COMPANY.city}, {COMPANY.country}.
        We are the controller of the personal data described here within the
        meaning of Article 4(7) of the General Data Protection Regulation
        (Regulation (EU) 2016/679, “GDPR”).
      </p>
      <p>
        For anything in this policy — including a request to exercise your
        rights — write to{" "}
        <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>. We have not
        appointed a Data Protection Officer, as we are not required to.
      </p>

      <h2>What we hold, why, and on what basis</h2>
      <dl>
        <div>
          <dt>Your account</dt>
          <dd>
            A username, a display name, and — if you gave one — an email
            address. Used to create and operate the account, to verify it, and
            to let you back in. <em>Basis:</em> performance of our contract with
            you, Article 6(1)(b).
          </dd>
        </div>
        <div>
          <dt>Your password</dt>
          <dd>
            Never in a form we can read. What we store is a hash. The key that
            unlocks your photographs is derived from your password on your own
            device and is not sent to us. <em>Basis:</em> Article 6(1)(b).
          </dd>
        </div>
        <div>
          <dt>Your photographs and videos</dt>
          <dd>
            Stored as encrypted files. Each one is encrypted on the device it
            came from before it is uploaded, and the key is wrapped to the
            members of the group it belongs to. What our storage receives, and
            what our staff see in the ordinary course of running this service,
            is ciphertext together with its size and arrival time. See{" "}
            <a href="#limits">The limits of that</a>. <em>Basis:</em> Article
            6(1)(b).
          </dd>
        </div>
        <div>
          <dt>Your profile picture</dt>
          <dd>
            <strong>Not encrypted.</strong> It has to be visible in places where
            there is no group — a sidebar, a member list — and sealing it per
            group would make it unopenable exactly where it is shown. It is the
            one image on this service that we can see, and you choose it knowing
            that. <em>Basis:</em> Article 6(1)(b).
          </dd>
        </div>
        <div>
          <dt>Album titles, names you give people, comments</dt>
          <dd>
            Encrypted to the group, like the photographs. The recognition that
            finds faces runs in your browser, on pictures it already has open;
            no face measurement is sent to us. <em>Basis:</em> Article 6(1)(b).
          </dd>
        </div>
        <div>
          <dt>Ordinary server logs</dt>
          <dd>
            Requests, timings, IP addresses and errors, so the service can be
            operated, faults diagnosed, and abuse stopped. No analytics, no
            advertising identifiers, no third-party trackers on this site.{" "}
            <em>Basis:</em> our legitimate interest in operating and securing
            the service, Article 6(1)(f).
          </dd>
        </div>
        <div>
          <dt>The waitlist</dt>
          <dd>
            If you asked to be told when there is room: your email address, the
            date you asked, and the language you were reading, held in the clear
            because it has to be readable to send you mail. We write once when a
            place opens. <em>Basis:</em> Article 6(1)(f), and you can have the
            row deleted by replying or writing to us.
          </dd>
        </div>
      </dl>

      <h2 id="limits">The limits of that</h2>
      <p>
        Encryption on your device is a real protection and it is the reason this
        service is built the way it is. It is not a claim that opening your
        files is impossible for us in every circumstance, and we would rather
        say so here than have you discover it later.
      </p>
      <p>
        <strong>A group’s recovery key.</strong> A group can keep a recovery
        copy of its key. Where it does, a member’s browser seals that key to a
        recovery public key whose private half is held offline, away from the
        servers that run the service. It exists so that a forgotten password
        does not destroy a group’s photographs. Where a recovery copy exists,
        there is a key in the world that can open that group. It is not used in
        the ordinary running of the service, and using it is a deliberate act
        rather than something the system does by itself.
      </p>
      <p>
        <strong>Lawful requests.</strong> Where we receive a binding order from
        a court, prosecutor, or law-enforcement or state-security authority
        competent to issue it, and where the group in question has a recovery
        copy of its key, we may be compelled to use it to make specific content
        available to that authority. We will do so only to the extent the order
        requires. Content decrypted in this way, and a record of the request,
        are retained in our systems for as long as the relevant proceedings and
        our legal obligations require, and are not used for any other purpose.{" "}
        <em>Basis:</em> compliance with a legal obligation, Article 6(1)(c).
      </p>
      <p>
        <strong>Where there is no recovery copy</strong> and the password is
        forgotten, the photographs stay locked. Nothing on our side can open
        them.
      </p>
      <p className="fc-legal__note">
        We will tell you about a request that concerns your account unless we
        are legally prohibited from doing so, or unless telling you would
        obstruct an investigation into serious harm.
      </p>

      <h2>Who processes it for us</h2>
      <p>
        We use a small number of suppliers, each engaged as a processor under
        Article 28 and each contractually bound to act only on our instructions:
      </p>
      <ul>
        <li>
          <strong>Cloudflare</strong> — object storage for the encrypted files,
          and hosting for this website.
        </li>
        <li>
          <strong>Railway</strong> — hosting for the application server.
        </li>
        <li>
          <strong>Neon</strong> — the managed Postgres database.
        </li>
        <li>
          <strong>Resend</strong> — sending transactional email, such as sign-in
          and verification messages.
        </li>
      </ul>
      <p>
        None of them is given a key to your photographs, and none of them
        receives data for their own purposes. We do not sell data and we do not
        share it for advertising.
      </p>

      <h2>Where it is stored</h2>
      <p>
        The database is hosted in the European Union. Encrypted files are held
        in Cloudflare’s object storage, which places objects automatically and
        does not guarantee a particular region; some may therefore rest outside
        the European Economic Area. Our email and hosting suppliers are
        established in the United States and may process data there.
      </p>
      <p>
        Where personal data is transferred outside the EEA, it is done under the
        safeguards in Chapter V of the GDPR — an adequacy decision where one
        covers the recipient, and the European Commission’s standard contractual
        clauses otherwise. Ask us and we will tell you which applies to a given
        supplier.
      </p>

      <h2>How long we keep it</h2>
      <ul>
        <li>
          <strong>Account data</strong> — until you delete the account, then
          removed within 30 days.
        </li>
        <li>
          <strong>Photographs and videos</strong> — until you or your group
          delete them. Deleted items sit in a trash for 30 days so a mistake can
          be undone, and are then purged.
        </li>
        <li>
          <strong>Server logs</strong> — kept briefly for operation and
          security, and not beyond 12 months.
        </li>
        <li>
          <strong>Waitlist entries</strong> — until you are invited or ask to be
          removed.
        </li>
        <li>
          <strong>Records connected to a lawful request</strong> — for as long
          as the proceedings and our legal obligations require.
        </li>
      </ul>

      <h2>Your rights</h2>
      <p>Under the GDPR you may:</p>
      <ul>
        <li>
          ask what we hold about you and get a copy of it — Articles 15 and 20;
        </li>
        <li>have inaccurate data corrected — Article 16;</li>
        <li>have data erased, including your whole account — Article 17;</li>
        <li>
          ask us to restrict processing, or object to it — Articles 18 and 21;
        </li>
        <li>
          withdraw a consent at any time, without affecting what was done before
          — Article 7(3).
        </li>
      </ul>
      <p>
        Write to <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a> and we
        will answer within one month. Be aware that because of the encryption,
        much of the honest answer to “what do you hold about me” is “encrypted
        files we do not open”: we can list them, tell you their size and dates,
        and delete them, but we cannot produce their contents for you. Your own
        copy of the app can, because you have the key.
      </p>
      <p>
        There is no automated decision-making or profiling in this service
        within the meaning of Article 22.
      </p>

      <h2>Complaints</h2>
      <p>
        If you think we have handled your data wrongly, please tell us first —
        it is usually the fastest way to fix it. You also have the right to
        complain to a supervisory authority. Ours is the President of the
        Personal Data Protection Office (Prezes Urzędu Ochrony Danych
        Osobowych), ul. Stawki 2, 00-193 Warsaw, Poland. You may also complain
        to the authority in the EU country where you live.
      </p>

      <h2>Security, and being straight about it</h2>
      <p>
        Files are encrypted with AES-256-GCM on the device they came from, and
        the keys that open them are wrapped to group members with RSA-OAEP.
        Passwords are stretched before they derive anything. Transport is TLS
        throughout. Access to production systems is limited and logged.
      </p>
      <p>
        No service is unbreakable, and we will not pretend otherwise. What we
        commit to is this: we make every reasonable effort to prevent a breach,
        and any weakness we learn of — from our own review, a supplier, or
        someone who reports one — is addressed as a priority as soon as we know
        about it. If a breach occurs that is likely to result in a risk to your
        rights and freedoms, we will notify the supervisory authority within 72
        hours as required by Article 33, and we will tell you directly where
        Article 34 requires it.
      </p>
      <p>
        If you have found a security flaw, please write to{" "}
        <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>. Reporting one
        in good faith is welcome and we will not treat it as an attack.
      </p>

      <h2>Keep your own copies</h2>
      <p>
        Please do not treat this service as the only place your photographs
        exist. Download your originals from time to time and keep a copy
        somewhere you control. That is good advice about every cloud service,
        including the largest ones, and it is doubly true of one that cannot
        reconstruct what it cannot read.
      </p>

      <h2>Children</h2>
      <p>
        This service is not intended for anybody under 16, and we do not
        knowingly create accounts for children. If you believe a child has an
        account here, write to us and we will remove it.
      </p>

      <h2>Changes</h2>
      <p>
        If this policy changes in a way that affects what we hold, why we hold
        it, or who processes it, we will say so before the change takes effect
        rather than afterwards.
      </p>
    </LegalShell>
  );
}

/* ════════════════════════════════ Terms ══════════════════════════════════ */

export function TermsPage() {
  return (
    <LegalShell title="Terms of service" updated={UPDATED}>
      <p className="fc-legal__lead">
        Plain terms for a small service. Use it for your own photographs, do not
        use it to harm anybody, keep your own backups, and understand what we
        can and cannot recover.
      </p>

      <h2>Who you are contracting with</h2>
      <p>
        {PRODUCT_NAME} is provided by <strong>{COMPANY.legalName}</strong>,{" "}
        {COMPANY.street}, {COMPANY.postcode} {COMPANY.city}, {COMPANY.country},
        contactable at <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.
        “We” and “us” below mean that company; “you” means the person holding
        the account. By creating an account you agree to these terms and to the{" "}
        <Link to="/privacy">privacy policy</Link>.
      </p>

      <h2>The account</h2>
      <p>
        Accounts are invite-only and personal to you. You are responsible for
        what happens under yours, which in practice means keeping your password
        to yourself — we cannot tell an intruder from you, because we cannot see
        inside either of your sessions.
      </p>
      <p>
        You must be 16 or older to hold an account. Your password is also the
        root of your encryption: if you lose it and your group has no recovery
        copy of its key, nobody can restore access, including us.
      </p>

      <h2>Your photographs stay yours</h2>
      <p>
        You keep every right in what you upload. We take no licence to it beyond
        what is technically necessary to store it, transmit it between the
        people you have shared it with, and hand it back to you. We do not train
        anything on it and we do not use it to advertise.
      </p>

      <h2>What you may not do</h2>
      <ul>
        <li>
          Upload material you have no right to share, or that is unlawful where
          you or your group members are.
        </li>
        <li>
          Upload sexual material involving children. Accounts doing this are
          removed and reported, and we will co-operate with any lawful request.
        </li>
        <li>
          Use the service to harass somebody, or to store somebody else’s
          private photographs against their wishes.
        </li>
        <li>
          Attack the service — probe it, overload it, or try to reach data that
          is not yours. Reporting a flaw you have found in good faith is welcome
          and is not this.
        </li>
        <li>Resell the service or access it by automated means at scale.</li>
      </ul>
      <p className="fc-legal__note">
        We do not scan your files, so enforcement follows reports rather than
        inspection. That is a consequence of the design and we are not going to
        pretend otherwise.
      </p>

      <h2>Lawful requests</h2>
      <p>
        We comply with binding orders from authorities competent to issue them.
        Where a group has kept a recovery copy of its key, such an order may
        require us to use it to make specific content available. What that means
        in practice — including that content decrypted this way is retained — is
        set out in the <Link to="/privacy">privacy policy</Link>, and you should
        read that section before deciding whether to enable a recovery key.
      </p>

      <h2>Groups</h2>
      <p>
        A group belongs to the people in it. An owner can remove members and
        delete the group; members can leave at any time. Anything you uploaded
        stays in the group after you leave unless you delete it first — the
        others hold keys to it, and it is as much their trip as yours.
      </p>

      <h2>Storage and plans</h2>
      <p>
        The free plan is real and is not a trial. Paid plans are not switched on
        yet; when they are, the price you agreed is the price you keep until you
        are given notice of a change, and you may cancel rather than accept it.
      </p>
      <p>
        If a group is over its storage limit, uploads stop. Nothing is deleted
        for being over the line without telling you first.
      </p>
      <p>
        Where you are a consumer in the EU buying a paid plan, you have the
        usual 14-day right to withdraw. If you ask us to start the service
        immediately, that right ends once it is fully performed, and until then
        you pay only for what you used.
      </p>

      <h2>Backups are your responsibility too</h2>
      <p>
        <strong>
          Please do not rely on this service as the only copy of your
          photographs.
        </strong>{" "}
        We back up what can be backed up and we run this carefully, but no
        online service should be anybody’s sole archive. Download your originals
        periodically and keep a copy on hardware you control. The export is
        always available and never behind a paywall, precisely so that this is
        easy to do.
      </p>

      <h2>Security: what we do, and what we can promise</h2>
      <p>
        We make every reasonable effort to keep this service secure — end-to-end
        encryption of your files, limited and logged production access, and
        prompt remediation of any weakness we become aware of, from wherever we
        learn of it.
      </p>
      <p>
        What we cannot promise is that no attack will ever succeed. Nobody
        honestly can. Except where the law does not allow it to be excluded, we
        are not liable for loss arising from a security incident, from
        unauthorised access to your account, or from your own device or password
        being compromised.
      </p>
      <p className="fc-legal__note">
        To be clear about what that clause does <em>not</em> do: it does not
        remove our liability under Article 82 GDPR for damage caused by our own
        infringement of data protection law, and it does not remove liability
        for death or personal injury, for intentional harm, or for gross
        negligence. None of those can lawfully be excluded, and we are not going
        to write a clause that pretends otherwise.
      </p>

      <h2>Availability and liability</h2>
      <p>
        The service is provided as it is. We do not promise it is never
        unavailable, and we may take it down for maintenance. To the extent the
        law allows, we are not liable for indirect or consequential loss, lost
        profits, or lost data where you had the ability to keep your own copy.
        Where liability cannot be excluded and can be limited, it is limited to
        the amount you paid us in the twelve months before the event — which,
        while the service is free, is nothing. Your statutory rights as a
        consumer are unaffected by any of this.
      </p>

      <h2>Ending it</h2>
      <p>
        Delete your account whenever you like. We may suspend or close an
        account that breaks the rules above, and will say why unless the law
        prevents it. Export first: after deletion the keys are gone, and with
        them, permanently, the ability to open anything.
      </p>

      <h2>Changes to these terms</h2>
      <p>
        We will give notice of a material change before it takes effect. If you
        do not accept it, you may close your account, and for a paid plan we
        will refund the unused part of what you have paid.
      </p>

      <h2>Law and disputes</h2>
      <p>
        These terms are governed by Polish law. If you are a consumer, that does
        not deprive you of the protection of the mandatory rules of the country
        you live in, and you may bring proceedings in your own courts. Disputes
        we cannot settle between us go to the courts having jurisdiction over{" "}
        {COMPANY.city}, {COMPANY.country}.
      </p>
      <p>
        Consumers in the EU may also use the European Commission’s online
        dispute resolution platform at{" "}
        <a
          href="https://ec.europa.eu/consumers/odr"
          target="_blank"
          rel="noreferrer"
        >
          ec.europa.eu/consumers/odr
        </a>
        . We are happy to try to resolve anything by email first.
      </p>

      <h2>Contact</h2>
      <p>
        {COMPANY.legalName}
        <br />
        {COMPANY.street}
        <br />
        {COMPANY.postcode} {COMPANY.city}
        <br />
        {COMPANY.country}
        <br />
        <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
      </p>
    </LegalShell>
  );
}

/* ════════════════════════════════ Shell ══════════════════════════════════ */

function LegalShell({
  title,
  updated,
  children,
}: {
  title: string;
  updated: string;
  children: React.ReactNode;
}) {
  return (
    <div className="fc-legal">
      <header className="fc-legal__head">
        <Link to="/" className="fc-legal__back">
          ← {PRODUCT_NAME}
        </Link>
        <h1>{title}</h1>
        <p className="fc-legal__updated">Last updated {updated}</p>
      </header>

      <article className="fc-legal__body">{children}</article>

      <footer className="fc-legal__foot">
        <Link to="/privacy">Privacy</Link>
        <Link to="/terms">Terms</Link>
        <Link to="/">Home</Link>
      </footer>
    </div>
  );
}
